import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import './Initial4RDisposition.css';
import './IntegrationApplicationMasterData.css';

interface ApplicationSummary {
  application_id: string;
  application_name: string;
  application_id_code: string;
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain' | null;
  overall_score: number | null;
  assessment_status: 'Not Started' | 'In-Process' | 'Completed';
  application_owner: string;
  business_criticality: string;
  completion_percentage: number;
  answered_questions: number;
  total_questions: number;
}

const Initial4RDisposition: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<ApplicationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data from database on component mount and set up auto-refresh
  useEffect(() => {
    loadApplicationData(true); // Initial load
    
    // Set up auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      loadApplicationData(false); // Background refresh
    }, 30000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(refreshInterval);
  }, []);

  // Refresh data when the window/tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadApplicationData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadApplicationData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      
      // Load both applications and scoring portfolio data
      const [applications, scoringData] = await Promise.all([
        apiService.getApplications(),
        apiService.getScoringPortfolioData()
      ]);
      
      // Create a map of scoring data for quick lookup
      const scoringMap = new Map();
      if (scoringData && Array.isArray(scoringData)) {
        scoringData.forEach((score: any) => {
          scoringMap.set(score.application_id, score);
        });
      }
      
      // Transform the data to match our interface, merging with scoring data
      const transformedApplications: ApplicationSummary[] = applications.map((app: any) => {
        const scoreData = scoringMap.get(app.id);
        
        return {
          application_id: app.id,
          application_name: app.name,
          application_id_code: app.applicationIdCode || app.id,
          recommendation: scoreData?.recommendation || app.recommendation || null,
          overall_score: scoreData?.overall_score || app.overallScore || null,
          assessment_status: scoreData?.assessment_status || 'Not Started',
          application_owner: app.applicationOwner || 'Not assigned',
          business_criticality: app.businessCriticality || 'Not specified',
          completion_percentage: scoreData?.completion_percentage || 0,
          answered_questions: scoreData?.answered_questions || 0,
          total_questions: scoreData?.total_questions || 0,
        };
      });

      setApplications(transformedApplications);
    } catch (err) {
      console.error('Error loading application data:', err);
      setError('Failed to load application data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  const getRecommendationStats = () => {
    const stats = applications.reduce((acc, app) => {
      if (app.recommendation) {
        acc[app.recommendation] = (acc[app.recommendation] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
    
    return {
      retire: stats['Retire'] || 0,
      rePlatform: stats['Re-Platform'] || 0,
      reDesign: stats['Re-Design'] || 0,
      retain: stats['Retain'] || 0,
      total: applications.length
    };
  };


  const stats = getRecommendationStats();

  // Loading state
  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Initial 4R Disposition</h1>
              <p className="page-header__subtitle">Loading application data...</p>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            fontSize: '18px',
            color: '#666'
          }}>
            Loading applications...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Initial 4R Disposition</h1>
              <p className="page-header__subtitle">Error loading data</p>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            gap: '20px'
          }}>
            <div style={{ fontSize: '18px', color: '#dc2626' }}>⚠️ {error}</div>
            <button
              onClick={() => loadApplicationData(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Initial 4R Disposition</h1>
            <p className="page-header__subtitle">
              Overview of application assessment progress and initial 4R recommendations
              {refreshing && (
                <span style={{ 
                  marginLeft: '10px', 
                  fontSize: '12px', 
                  color: '#1976d2',
                  fontWeight: '500'
                }}>
                  🔄 Updating...
                </span>
              )}
            </p>
          </div>
          <div className="page-header__actions">
            <button
              onClick={() => loadApplicationData(false)}
              disabled={loading || refreshing}
              style={{
                padding: '10px 16px',
                backgroundColor: (loading || refreshing) ? '#ccc' : '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (loading || refreshing) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🔄 {(loading || refreshing) ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">


      {/* Disposition Model */}
      <div className="md-card" style={{ marginTop: '20px' }}>
        <div className="md-card-content">
          <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>🎯 Disposition Model</h2>
          <div className="md-stats-grid">
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#dc2626' }}>
                  {stats.retire}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Retire</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {stats.total > 0 
                    ? `${Math.round((stats.retire / stats.total) * 100)}% of apps` 
                    : '0% of apps'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#10b981' }}>
                  {stats.retain}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Retain</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {stats.total > 0 
                    ? `${Math.round((stats.retain / stats.total) * 100)}% of apps` 
                    : '0% of apps'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#f59e0b' }}>
                  {stats.rePlatform}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Re-Platform</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {stats.total > 0 
                    ? `${Math.round((stats.rePlatform / stats.total) * 100)}% of apps` 
                    : '0% of apps'}
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated">
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#3b82f6' }}>
                  {stats.reDesign}
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Re-Design</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {stats.total > 0 
                    ? `${Math.round((stats.reDesign / stats.total) * 100)}% of apps` 
                    : '0% of apps'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Quick Actions */}
      <div className="md-card" style={{ marginTop: '20px' }}>
        <div className="md-card-content">
          <h2 style={{ marginBottom: '24px', color: '#1976d2' }}>🚀 Quick Actions</h2>
          <div className="md-stats-grid">
            <div className="md-card md-card--elevated" style={{ cursor: 'pointer' }} onClick={() => navigate('/evaluation/qa')}>
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#f59e0b', fontSize: '24px' }}>
                  ❓
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Portfolio Q&A</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Conduct assessments and gather data
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated" style={{ cursor: 'pointer' }} onClick={() => navigate('/application-portfolio-scoring')}>
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#3b82f6', fontSize: '24px' }}>
                  📊
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Portfolio Scoring</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Review scores and recommendations
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated" style={{ cursor: 'pointer' }} onClick={() => navigate('/apr-execution')}>
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#dc2626', fontSize: '24px' }}>
                  ▶️
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>APR Execution</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Execute APR models and processes
                </div>
              </div>
            </div>
            
            <div className="md-card md-card--elevated" style={{ cursor: 'pointer' }} onClick={() => navigate('/evaluation/reports')}>
              <div className="md-card-content">
                <div className="md-stat-primary" style={{ color: '#10b981', fontSize: '24px' }}>
                  📈
                </div>
                <div className="md-stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Export & Reports</div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Export data and generate reports
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Initial4RDisposition;